import React, { useEffect, useState } from 'react';

import AnimatedImage from 'core-lib/components/AnimatedImage';
import { Divider } from 'ui-v2/components/Divider';
import Modal from 'ui-v2/components/Modal';
import ShimmerImage from 'core-lib/components/ShimmerImage';
import { Typography } from 'ui-v2/components/Typography';
import { Button } from 'ui-v2/components/Button';
import { Input } from 'ui-v2/components/Input';
import s from './AddReviewPopup.module.scss';
import uploadFile from 'core-lib/services/uploadFile';
import { customToast } from 'core-lib/utils/customToast';
import { isStringValid, validateAndTrimString } from 'core-lib/utils/utils';
import postUserReview from 'src/modules/parkplusStoreV2/services/postUserReview';
import {
  trackCtaClickedStoreV2,
  trackPopupViewStoreV2,
} from 'src/modules/parkplusStoreV2/utils/events';
import Icon from 'ui-v2/components/Icon/Icon';

type AddReviewPopupPropType = {
  show: boolean;
  onClose: () => void;
  data: {
    title: string;
    subtitle: string;
    imgUrl: string;
    sku: string;
    id: number;
  };
  hideAdd: () => void;
};
export const AddReviewPopup = ({
  show,
  onClose,
  data,
  hideAdd,
}: AddReviewPopupPropType) => {
  const [title, setTitle] = useState({
    value: '',
    error: '',
  });
  const [description, setDescription] = useState({
    value: '',
    error: '',
  });
  const [starCount, setStarCount] = useState(4);
  const [mediaImageFiles, setMediaImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mediaImage, setMediaImage] = useState([]);
  const uploadImagesAndPost = async () => {
    setLoading(true);
    let images = [];
    for (let idx = 0; idx < mediaImageFiles.length; idx++) {
      let file = mediaImageFiles[idx];
      // file = await getCompressedImage(file, 0.8);

      if (file) {
        const { response, error } = await uploadFile({
          serviceName: 'parkplus-store',
          file,
          convertTo: 'webp',
        });

        if (response) {
          images.push(response);
        }

        if (error) {
          customToast({
            label: error?.message || 'Sorry! Error occured in uploading images',
            variant: 'criticalNotPriority',
          });
          setLoading(false);
          return;
        }
      }
    }
    handleSubmitApi(images);
  };
  const handleSubmit = () => {
    trackCtaClickedStoreV2('submit_review');
    if (!isStringValid(title.value)) {
      setTitle({
        ...title,
        error: 'please enter title',
      });
      return;
    }
    uploadImagesAndPost();
  };
  const handleSubmitApi = async (images) => {
    const payload = {
      sku: data?.sku,
      product_id: data?.id,
      rating: starCount,
      title: validateAndTrimString(title.value).isValid
        ? validateAndTrimString(title.value).trimmedStr
        : '',
      description: validateAndTrimString(description.value).isValid
        ? validateAndTrimString(description.value).trimmedStr
        : '',
      image_url: images,
    };
    const { response, error } = await postUserReview(payload);
    if (response) {
      onClose();
      hideAdd();
    } else {
      customToast({
        label:
          error?.message ||
          'Unable to post review right now, please try after sometime',
        variant: 'criticalNotPriority',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    trackPopupViewStoreV2('AddReviewPopup');
  }, [show]);

  return (
    <Modal
      show={show}
      head={{
        show: true,
        onClose: onClose,
      }}
      variant="page"
      rootStyle={{
        zIndex: '10',
      }}
      animation={{
        type: 'rtl',
      }}
      noPadding
    >
      <div className={s.cardWrapper}>
        <div className={s.card}>
          <ShimmerImage src={data?.imgUrl} className={s.img} />
          <div className={s.text}>
            <Typography label={data?.subtitle} variant="label_1_bold" />
            <Typography label={data?.title} variant="body_1_medium" />
          </div>
        </div>
        <Divider variant="full" />
        <div className={s.starContainer}>
          {new Array(5).fill(1).map((item, index) => (
            <div
              key={index}
              onClick={() => {
                setStarCount(index + 1);
              }}
            >
              <Icon name="StarBold"
                width={38}
                height={38}
                color={index + 1 <= starCount ? 'positive' : 'primary'}
              />
            </div>
          ))}
        </div>
      </div>
      <Divider variant="padded" />
      <div className={s.photoAndText}>
        <Typography label="Add photos of the Product" variant="h_4_bold" />
        <div className={s.imagecontainer}>
          {mediaImage.map((item, ind) => (
            <div
              className={s.image}
              key={ind}
              onClick={() => {
                setMediaImage((prev) => prev.filter((_, i) => i != ind));
                setMediaImageFiles((prev) => prev.filter((_, i) => i != ind));
              }}
            >
              <ShimmerImage src={item} className={s.image} />
              <div className={s.icon}>
                <Icon name="CrossRegular" width={16} height={16} color="brand" />
              </div>
            </div>
          ))}
          {mediaImageFiles.length < 4 && (
            <label>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target?.files[0]?.type?.split('/')[0] === 'image') {
                    const file = e.target.files[0];
                    const fileUrl = URL.createObjectURL(file);
                    setMediaImage((prev) => [...prev, fileUrl]);
                    setMediaImageFiles((prev) => [...prev, file]);
                  } else {
                    customToast({
                      label: 'Please upload a image',
                      variant: 'criticalNotPriority',
                    });
                  }
                }}
              />
              <AnimatedImage
                src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240408162742742788.png"
                className={s.addimage}
              />
            </label>
          )}
        </div>
        <Typography
          label="Add a Review"
          style={{
            marginBottom: '16px',
          }}
          variant="h_4_bold"
        />
        <div className={s.inputs}>
          <Input
            inputType="text"
            placeholder="Review Title"
            value={title.value}
            errorText={title.error}
            hasError={!!title.error}
            onChange={(e) => {
              const { value } = e.target as HTMLInputElement;
              setTitle({
                ...title,
                value: value,
                error: '',
              });
            }}
            maxLength={30}
          />
          <Input
            inputType="text"
            placeholder="Your review in 150 words max"
            isTextarea
            value={description.value}
            errorText={description.error}
            onChange={(e) => {
              const { value } = e.target as HTMLInputElement;
              setDescription({
                ...description,
                value: value,
                error: '',
              });
            }}
            inputClass={s.inputContainer}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      <div className={s.disclaimer}>
        <Typography label="Disclaimer: " variant="tiny_medium" />
        <Typography
          variant="tiny_medium"
          label=" All your feedbacks will be moderated by our team, once approved it will get reflected in the product reviews section"
        />
      </div>
      <div className={s.btn}>
        <Button
          variant="primary"
          size="medium"
          label="submit"
          fullWidth
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        />
      </div>
    </Modal>
  );
};
